import React, { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useRecordsPage, useMessageChannelAdapter, useBreadcrumbRouteMetaEmitter } from 'hooks';
import { useFreshdeskContextState } from './freshdesk-context';
import { recordPageController } from 'controllers';

const initialContextValue = {
  loading: true,
  entity: null,
  associatedEntities: [],
  record: null,
  error: null,
  recordsListData: {},
  nativeDropdownChoicesByFieldId: new Map(),
};

export const RecordPageStateContext = React.createContext(initialContextValue);
const RecordPageUpdaterContext = React.createContext();

const RecordPageProvider = props => {
  const { state } = useLocation();
  const { fetchRecordAndEntityIfNull, fetchRecord: getRecord } = useRecordsPage();
  const { isFeatureEnabled } = useFreshdeskContextState();
  const { getNativeDropdownChoices } = useMessageChannelAdapter();
  const useDropdownLookup = isFeatureEnabled('useDropdownLookup');
  // The actual native-dropdown-choices API call is made by the host application over the message channel
  // (see useMessageChannelAdapter) - recordPageController only owns building filter params and mapping the
  // response, so it's handed the host-backed fetcher here rather than calling a REST service itself.
  recordPageController.nativeDropdownDataSource = { getNativeDropdownChoices };
  // Tracks whether native dropdown choices have already been fetched for the record currently loaded -
  // reset on every new load, set true once the fetch below actually runs. If the flag was still false when
  // the main effect checked it, this stays false so the catch-up effect further down can retry once the
  // flag resolves true, instead of silently never fetching choices for that record view.
  const hasFetchedNativeDropdownChoicesRef = useRef(false);
  const { entity, associatedEntities, data: recordsListData } = state ?? { entity: null, associatedEntities: [] };
  const [recordPage, setRecordPage] = useState({ ...initialContextValue, entity, associatedEntities, recordsListData });
  const { displayId } = useParams();
  useBreadcrumbRouteMetaEmitter({
    entity: recordPage.entity,
    record: recordPage.record,
    displayId,
  });

  const fetchRecord = useCallback(async () => {
    setRecordPage(prev => {
      return {
        ...prev,
        loading: true,
      };
    });
    const recordData = await getRecord(recordPage.entity);
    setRecordPage(prev => {
      return {
        ...prev,
        ...recordData,
        loading: false,
      };
    });
  }, [recordPage.entity, getRecord]);

  useEffect(() => {
    hasFetchedNativeDropdownChoicesRef.current = false;
    // Guards against a fast entity change resolving a stale load after a newer one has already started -
    // without this, the stale fetch's native choices/record state could land after, and overwrite, the
    // current record's freshly-loading state.
    let isCancelled = false;
    (async () => {
      setRecordPage(prev => {
        return {
          ...prev,
          loading: true,
        };
      });
      const entityRecordData = await fetchRecordAndEntityIfNull(entity);
      if (isCancelled) return;
      if (Object.keys(entityRecordData).length > 0) {
        // Waits for the lookup-backed dropdown choices to resolve (accounts with useDropdownLookup only)
        // before marking the page loaded, so the record detail view renders resolved values straightaway
        // instead of rendering raw ids first and updating a moment later.
        let nativeDropdownChoicesByFieldId = initialContextValue.nativeDropdownChoicesByFieldId;
        const resolvedEntity = entityRecordData.entity ?? entity;
        if (useDropdownLookup && resolvedEntity?.fields) {
          const choicesResponse = await recordPageController.fetchNativeDropdownChoices(resolvedEntity.fields);
          if (isCancelled) return;
          if (choicesResponse?.success) {
            nativeDropdownChoicesByFieldId = choicesResponse.response;
          }
          hasFetchedNativeDropdownChoicesRef.current = true;
        }
        if (isCancelled) return;
        setRecordPage(prev => {
          return {
            ...prev,
            ...entityRecordData,
            nativeDropdownChoicesByFieldId,
            loading: false,
          };
        });
      }
    })();
    return () => {
      isCancelled = true;
    };
    // useDropdownLookup is intentionally omitted - depending on it directly would re-run this whole
    // fetchRecordAndEntityIfNull sequence a second time even when its resolved value never actually changes
    // for the account; the catch-up effect below covers the case where it resolves true after this ran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, fetchRecordAndEntityIfNull]);

  // Catch-up for the race the effect above can't see: if useDropdownLookup was still false when the load
  // ran (the Freshdesk context hadn't resolved the flag yet), that effect never re-runs on its own since
  // useDropdownLookup isn't in its deps - without this, native dropdown choices would be skipped permanently
  // for that record view instead of just delayed. Fires once the flag/entity are both available and hasn't
  // already fetched for the current record.
  useEffect(() => {
    if (!useDropdownLookup || hasFetchedNativeDropdownChoicesRef.current || recordPage.loading || !recordPage.entity?.fields) {
      return;
    }
    let isCancelled = false;
    hasFetchedNativeDropdownChoicesRef.current = true;
    (async () => {
      const choicesResponse = await recordPageController.fetchNativeDropdownChoices(recordPage.entity.fields);
      if (isCancelled) return;
      if (choicesResponse?.success) {
        setRecordPage(prev => ({ ...prev, nativeDropdownChoicesByFieldId: choicesResponse.response }));
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [useDropdownLookup, recordPage.loading, recordPage.entity]);

  const updateFunctions = useMemo(() => {
    return {
      fetchRecord,
      setRecordPage,
    };
  }, [fetchRecord, setRecordPage]);

  return (
    <RecordPageStateContext.Provider value={recordPage}>
      <RecordPageUpdaterContext.Provider value={updateFunctions}>{props.children}</RecordPageUpdaterContext.Provider>
    </RecordPageStateContext.Provider>
  );
};

const useRecordPageState = () => {
  const recordPageState = useContext(RecordPageStateContext);
  if (!recordPageState) {
    throw new Error('useRecordPageState must be used within a RecordPageProvider');
  }
  return recordPageState;
};

const useRecordPageUpdater = () => {
  const { setRecordPage, fetchRecord } = useContext(RecordPageUpdaterContext);
  if (!setRecordPage || !fetchRecord) {
    throw new Error('useRecordPageUpdater must be used within a RecordPageProvider');
  }

  return { setRecordPage, fetchRecord };
};

export { RecordPageProvider, useRecordPageState, useRecordPageUpdater };
